import { useCallback, useEffect, useState } from "react";
import type React from "react";
import { theme, btnPrimary, inputStyle, type as text } from "../theme";
import { fmtDate } from "../lib/format";
import { api, apiErrorMessage } from "../api/client";
import { DetailFields, EditButton } from "./RecordView";

// The AI impact assessment inside an AI system's panel — ISO/IEC 42001 6.1.4 and Annex B.5,
// informed by ISO/IEC 42005. Each dimension is rated likelihood by severity as it stands with its
// mitigation in place. The residual rating and each dimension's band are the server's
// (lib/aiImpactAssessment.ts) and are shown as returned; the page only multiplies the two numbers
// for the score beside a row, which is arithmetic, not a rule. An approved assessment is a record:
// it cannot be edited, a new one supersedes it. A draft reads as a record too until Edit is
// chosen, and only for someone who may edit (canEdit); saving, approving or discarding returns
// to the read-only view.

export interface ImpactCatalogue {
  impactDimensions: Array<{ id: string; label: string; guidance: string }>;
  likelihoodLabels: string[];
  severityLabels: string[];
  impactReviewMonths?: { standard: number; highRisk: number };
}

interface ImpactRow { dimension: string; likelihood: number | null; severity: number | null; mitigation: string | null; rating?: string | null }
interface Assessment {
  id: string; status: "draft" | "approved" | "superseded"; standing: "draft" | "approved" | "review_due" | "superseded";
  intendedUse: string | null; foreseeableMisuse: string | null; affectedParties: string | null;
  impacts: ImpactRow[]; residualRating: string | null; blockers: string[];
  approvedAt: string | null; approvedByName: string | null; reviewDueAt: string | null; createdAt: string;
  /** The DPIA covering the same processing — a cross-reference, settable on an approved record too. */
  dpiaId?: string | null;
}

const RATING: Record<string, { label: string; color: string; bg: string }> = {
  low:      { label: "Low",      color: theme.color.riskLowText,  bg: theme.color.riskLowBg },
  medium:   { label: "Medium",   color: theme.color.riskMedText,  bg: theme.color.riskMedBg },
  high:     { label: "High",     color: theme.color.riskHighText, bg: theme.color.riskHighBg },
  critical: { label: "Critical", color: theme.color.riskCritText, bg: theme.color.riskCritBg },
};

export function RatingBadge({ rating, empty = "Not fully rated" }: { rating: string | null | undefined; empty?: string }) {
  const r = rating ? RATING[rating] : null;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: r?.bg ?? theme.color.panelAlt, color: r?.color ?? theme.color.subtle }}>
      {r?.label ?? empty}
    </span>
  );
}

const day = fmtDate;
const small: React.CSSProperties = { ...inputStyle, fontSize: text.hint.fontSize, padding: "5px 8px" };
const quiet: React.CSSProperties = { fontSize: text.hint.fontSize, padding: "6px 12px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.text, cursor: "pointer", fontFamily: theme.font };

type Draft = { intendedUse: string; foreseeableMisuse: string; affectedParties: string; impacts: ImpactRow[] };

function toDraft(a: Assessment | null, dims: ImpactCatalogue["impactDimensions"]): Draft {
  return {
    intendedUse: a?.intendedUse ?? "", foreseeableMisuse: a?.foreseeableMisuse ?? "", affectedParties: a?.affectedParties ?? "",
    impacts: dims.map((d) => {
      const e = a?.impacts.find((x) => x.dimension === d.id);
      return { dimension: d.id, likelihood: e?.likelihood ?? null, severity: e?.severity ?? null, mitigation: e?.mitigation ?? "" };
    }),
  };
}

const payload = (d: Draft) => ({
  intendedUse: d.intendedUse.trim() || null,
  foreseeableMisuse: d.foreseeableMisuse.trim() || null,
  affectedParties: d.affectedParties.trim() || null,
  impacts: d.impacts.map((e) => ({ dimension: e.dimension, likelihood: e.likelihood, severity: e.severity, mitigation: e.mitigation?.trim() || null })),
});

export function AiImpactAssessmentPanel({ systemId, riskClass, catalogue, onChanged, canEdit = true, dpias }: {
  systemId: string;
  riskClass: string;
  catalogue: ImpactCatalogue;
  /** The organisation's DPIAs, for linking the one this assessment relies on. */
  dpias?: Array<{ id: string; name: string }>;
  /** Called after an approval or a new draft, so the register can refresh its tiles and attention. */
  onChanged?: () => void | Promise<void>;
  /** The viewer may start, edit, approve or discard an assessment; otherwise it is shown read-only. */
  canEdit?: boolean;
}) {
  const [assessments, setAssessments] = useState<Assessment[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  // The draft reads as a record until Edit is chosen; saving, approving or discarding returns to it.
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const dims = catalogue.impactDimensions;

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ assessments: Assessment[] }>(`/ai-systems/${systemId}/impact-assessments`);
      setAssessments(res.assessments);
      const d = res.assessments.find((a) => a.status === "draft") ?? null;
      setDraft(d ? toDraft(d, dims) : null);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the impact assessments."));
      setAssessments([]);
    }
  }, [systemId, dims]);

  useEffect(() => { void load(); }, [load]);

  if (!assessments) return <div style={text.hint}>Loading impact assessments…</div>;

  const current = assessments.find((a) => a.status === "approved") ?? null;
  const pending = assessments.find((a) => a.status === "draft") ?? null;
  const history = assessments.filter((a) => a.status === "superseded");
  const high = riskClass === "high" || riskClass === "prohibited";
  const months = catalogue.impactReviewMonths ? (high ? catalogue.impactReviewMonths.highRisk : catalogue.impactReviewMonths.standard) : null;

  async function run(fn: () => Promise<unknown>, fallback: string, editAfter: boolean) {
    setBusy(true); setError("");
    try { await fn(); await load(); setEditing(editAfter); await onChanged?.(); }
    catch (e) {
      // Approving saves first, so a refused approval has still saved the draft: read it back, or the list
      // of what is missing keeps naming what was just filled in (RES-22).
      const message = apiErrorMessage(e, fallback);
      await load();
      setError(message);
    }
    finally { setBusy(false); }
  }

  // Starting a draft opens it for editing; every other move returns to the read-only view.
  const start = () => run(() => api.post(`/ai-systems/${systemId}/impact-assessments`, payload(toDraft(current, dims))), "Could not start an assessment.", true);
  const saveDraft = () => pending && draft && run(() => api.put(`/ai-systems/${systemId}/impact-assessments/${pending.id}`, payload(draft)), "Could not save the draft.", false);
  const approve = () => pending && draft && run(async () => {
    // Save first, so what is approved is what is on screen.
    await api.put(`/ai-systems/${systemId}/impact-assessments/${pending.id}`, payload(draft));
    await api.post(`/ai-systems/${systemId}/impact-assessments/${pending.id}/approve`, {});
  }, "Could not approve the assessment.", false);
  const discard = () => pending && run(() => api.del(`/ai-systems/${systemId}/impact-assessments/${pending.id}`), "Could not discard the draft.", false);
  /** Leave the editor without saving: the draft goes back to what was last saved. */
  const cancelEdit = () => { setDraft(pending ? toDraft(pending, dims) : null); setEditing(false); setError(""); };
  const labelOf = (labels: string[], n: number | null) => (n ? `${n} · ${labels[n] ?? n}` : "Not rated");

  const setImpact = (i: number, patch: Partial<ImpactRow>) =>
    setDraft((d) => (d ? { ...d, impacts: d.impacts.map((e, j) => (j === i ? { ...e, ...patch } : e)) } : d));

  return (
    <div>
      <div style={{ ...text.hint, marginBottom: 10 }}>
        What the system could do to the people it affects, rated likelihood by severity with its mitigation in place
        (ISO/IEC 42001 6.1.4, Annex B.5). Approved by someone other than its author, and reviewed
        {months ? ` every ${months} months` : " on a cycle"}{high ? " because the system is high risk" : ""}.
      </div>

      {current ? (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "8px 10px", borderRadius: 3, background: theme.color.panelAlt, marginBottom: 10 }}>
          <span style={{ ...text.itemTitle, fontSize: text.hint.fontSize }}>Approved assessment</span>
          <RatingBadge rating={current.residualRating} />
          <span style={text.hint}>
            by {current.approvedByName ?? "—"} on {day(current.approvedAt)} · review {current.standing === "review_due" ? "was due" : "due"} {day(current.reviewDueAt)}
          </span>
          {current.standing === "review_due" && <span style={{ ...text.meta, fontWeight: 700, color: theme.color.dangerText }}>Review overdue</span>}
          {/* The DPIA answers whether the personal data processing is lawful and proportionate; this
              assessment does not, so a system processing personal data needs both, linked. */}
          {(canEdit || current.dpiaId) && (
            <label style={{ ...text.hint, display: "flex", gap: 6, alignItems: "center", marginLeft: "auto" }}>
              DPIA relied on
              {canEdit ? (
                <select
                  value={current.dpiaId ?? ""}
                  disabled={busy || !dpias}
                  onChange={(e) => void run(() => api.put(`/ai-systems/${systemId}/impact-assessments/${current.id}/dpia`, { dpiaId: e.target.value || null }), "Could not link the DPIA.", false)}
                  style={small}
                >
                  <option value="">None</option>
                  {current.dpiaId && !dpias?.some((d) => d.id === current.dpiaId) && <option value={current.dpiaId}>Linked DPIA</option>}
                  {(dpias ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              ) : (
                <span style={{ color: theme.color.text }}>{dpias?.find((d) => d.id === current.dpiaId)?.name ?? "Linked DPIA"}</span>
              )}
            </label>
          )}
        </div>
      ) : (
        <div style={{ ...text.hint, color: high ? theme.color.dangerText : theme.color.subtle, marginBottom: 10, fontWeight: high ? 600 : 400 }}>
          No approved impact assessment{high ? " — expected for a high-risk system." : "."}
        </div>
      )}

      {!pending && canEdit && (
        <EditButton
          onClick={() => { if (!busy) void start(); }}
          label={busy ? "Starting…" : current ? "Start a review (new version from the approved one)" : "Start an impact assessment"}
        />
      )}

      {pending && draft && !(editing && canEdit) && (
        <div style={{ border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
            <span style={{ ...text.itemTitle, fontSize: text.hint.fontSize, display: "flex", gap: 6, alignItems: "center" }}>
              Draft assessment <RatingBadge rating={pending.residualRating} />
            </span>
            {canEdit && <EditButton onClick={() => setEditing(true)} label="Edit draft" />}
          </div>
          <DetailFields fields={[
            { label: "Intended use", value: pending.intendedUse, wide: true, multiline: true },
            { label: "Reasonably foreseeable misuse", value: pending.foreseeableMisuse, wide: true, multiline: true },
            { label: "Who is affected", value: pending.affectedParties, wide: true, multiline: true },
          ]} />
          <div style={{ overflowX: "auto", marginTop: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.hint.fontSize, minWidth: 620 }}>
              <thead>
                <tr style={{ textAlign: "left", color: text.label.color, borderBottom: "1px solid var(--c-line)" }}>
                  <th style={{ padding: "4px 6px", fontWeight: 600 }}>Dimension</th>
                  <th style={{ padding: "4px 6px", fontWeight: 600 }}>Likelihood</th>
                  <th style={{ padding: "4px 6px", fontWeight: 600 }}>Severity</th>
                  <th style={{ padding: "4px 6px", fontWeight: 600 }}>Score</th>
                  <th style={{ padding: "4px 6px", fontWeight: 600 }}>Mitigation in place</th>
                </tr>
              </thead>
              <tbody>
                {dims.map((d) => {
                  const e = pending.impacts.find((x) => x.dimension === d.id);
                  return (
                    <tr key={d.id} style={{ borderTop: `1px solid ${theme.color.border}`, verticalAlign: "top", color: theme.color.text }}>
                      <td style={{ padding: "6px", width: "26%", fontWeight: 600, color: theme.color.heading }}>{d.label}</td>
                      <td style={{ padding: "6px" }}>{labelOf(catalogue.likelihoodLabels, e?.likelihood ?? null)}</td>
                      <td style={{ padding: "6px" }}>{labelOf(catalogue.severityLabels, e?.severity ?? null)}</td>
                      <td style={{ padding: "6px", whiteSpace: "nowrap" }}>
                        <div style={{ fontWeight: 600 }}>{e?.likelihood && e?.severity ? e.likelihood * e.severity : "—"}</div>
                        {e?.rating && <RatingBadge rating={e.rating} />}
                      </td>
                      <td style={{ padding: "6px", whiteSpace: "pre-wrap" }}>{e?.mitigation || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pending.blockers.length > 0 && (
            <ul style={{ ...text.hint, margin: "10px 0 0", paddingLeft: 18, color: theme.color.warningText }}>
              {pending.blockers.map((b) => <li key={b}>{b}</li>)}
            </ul>
          )}
        </div>
      )}

      {pending && draft && editing && canEdit && (
        <div style={{ border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
            <span style={{ ...text.itemTitle, fontSize: text.hint.fontSize }}>Draft assessment</span>
            <span style={{ ...text.meta, color: theme.color.subtle, display: "flex", gap: 6, alignItems: "center" }}>
              Residual rating (last saved) <RatingBadge rating={pending.residualRating} />
            </span>
          </div>
          <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
            {([
              ["intendedUse", "Intended use", "What it is for, who uses it and in what decisions."],
              ["foreseeableMisuse", "Reasonably foreseeable misuse", "How it could be used in ways it was not meant for."],
              ["affectedParties", "Who is affected", "Individuals and groups its output reaches, including anyone vulnerable."],
            ] as const).map(([key, label, ph]) => (
              <label key={key} style={{ ...text.label, marginBottom: 0, display: "grid", gap: 4 }}>
                {label}
                <textarea value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} placeholder={ph} style={{ ...inputStyle, fontSize: text.hint.fontSize, fontWeight: 400, minHeight: 44, resize: "vertical" }} />
              </label>
            ))}
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.hint.fontSize, minWidth: 620 }}>
              <thead>
                <tr style={{ textAlign: "left", color: text.label.color, borderBottom: "1px solid var(--c-line)" }}>
                  <th style={{ padding: "4px 6px", fontWeight: 600 }}>Dimension</th>
                  <th style={{ padding: "4px 6px", fontWeight: 600 }}>Likelihood</th>
                  <th style={{ padding: "4px 6px", fontWeight: 600 }}>Severity</th>
                  <th style={{ padding: "4px 6px", fontWeight: 600 }}>Score</th>
                  <th style={{ padding: "4px 6px", fontWeight: 600 }}>Mitigation in place</th>
                </tr>
              </thead>
              <tbody>
                {draft.impacts.map((e, i) => {
                  const dim = dims.find((d) => d.id === e.dimension);
                  const saved = pending.impacts.find((x) => x.dimension === e.dimension);
                  return (
                    <tr key={e.dimension} style={{ borderTop: `1px solid ${theme.color.border}`, verticalAlign: "top" }}>
                      <td style={{ padding: "6px", width: "26%" }}>
                        <div style={{ fontWeight: 600, color: theme.color.heading }}>{dim?.label ?? e.dimension}</div>
                        {dim?.guidance && <div style={{ ...text.meta, marginTop: 2 }}>{dim.guidance}</div>}
                      </td>
                      {(["likelihood", "severity"] as const).map((k) => (
                        <td key={k} style={{ padding: "6px" }}>
                          <select
                            aria-label={`${dim?.label ?? e.dimension} ${k}`}
                            value={e[k] ?? ""}
                            onChange={(ev) => setImpact(i, { [k]: ev.target.value ? Number(ev.target.value) : null })}
                            style={small}
                          >
                            <option value="">Not rated</option>
                            {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} · {(k === "likelihood" ? catalogue.likelihoodLabels : catalogue.severityLabels)[n] ?? n}</option>)}
                          </select>
                        </td>
                      ))}
                      <td style={{ padding: "6px", whiteSpace: "nowrap" }}>
                        <div style={{ fontWeight: 600, color: theme.color.text }}>{e.likelihood && e.severity ? e.likelihood * e.severity : "—"}</div>
                        {saved?.rating && saved.likelihood === e.likelihood && saved.severity === e.severity && <RatingBadge rating={saved.rating} />}
                      </td>
                      <td style={{ padding: "6px" }}>
                        <textarea
                          aria-label={`${dim?.label ?? e.dimension} mitigation`}
                          value={e.mitigation ?? ""}
                          onChange={(ev) => setImpact(i, { mitigation: ev.target.value })}
                          style={{ ...small, minHeight: 34, resize: "vertical" }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pending.blockers.length > 0 && (
            <ul style={{ ...text.hint, margin: "10px 0 0", paddingLeft: 18, color: theme.color.warningText }}>
              {pending.blockers.map((b) => <li key={b}>{b}</li>)}
            </ul>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <button type="button" onClick={saveDraft} disabled={busy} style={{ ...quiet, opacity: busy ? 0.6 : 1 }}>Save draft</button>
            <button type="button" onClick={approve} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} title="Someone other than the author approves, unless you are the organisation's only member">
              Approve
            </button>
            <button type="button" onClick={cancelEdit} disabled={busy} style={quiet}>Cancel</button>
            <button type="button" onClick={discard} disabled={busy} style={{ ...quiet, color: theme.color.dangerText, borderColor: theme.color.danger, marginLeft: "auto" }}>Discard draft</button>
          </div>
        </div>
      )}

      {error && <div style={{ ...text.hint, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>}

      {history.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button type="button" onClick={() => setShowHistory(!showHistory)} aria-expanded={showHistory} style={{ fontSize: text.hint.fontSize, background: "transparent", border: "none", color: theme.color.accent, cursor: "pointer", padding: 0, fontFamily: theme.font }}>
            {showHistory ? "Hide" : "Show"} {history.length} earlier assessment{history.length === 1 ? "" : "s"}
          </button>
          {showHistory && (
            <ul style={{ ...text.hint, margin: "6px 0 0", paddingLeft: 18, lineHeight: 1.7 }}>
              {history.map((h) => (
                <li key={h.id}>
                  Approved {day(h.approvedAt)} by {h.approvedByName ?? "—"}, residual <RatingBadge rating={h.residualRating} />, superseded
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
